import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Button, ButtonGroup, Card, CardGrid, Div, Group, Header, Panel, Progress, SimpleCell, Skeleton, Tabs, TabsItem, Text, Title } from '@vkontakte/vkui';
import { Icon28BookOutline, Icon28CheckCircleOutline, Icon28LockOutline, Icon28SchoolOutline } from '@vkontakte/icons';
import { AppShell, ScreenHeader } from '../../components/Layout/AppShell';
import { EmptyState } from '../../components/States/States';
import { CATEGORY_PANEL } from '../../routing/routing';
import { categoryImagePath } from '../../utils/assets';
import {
  getCategoryDescription,
  getCategoryProgress,
  getDifficultyMeta,
  getTrainingCount,
  getViewedWordIDs,
  consumeCategoryScrollPosition
} from '../../utils/categoryActivity';
import { fitTextSize, pluralize } from '../../utils/text';

const filters = [
  { id: 'all', label: 'Все слова' },
  { id: 'new', label: 'Новые' },
  { id: 'saved', label: 'Сохранённые' }
];

function filterWords(words, filterID, viewedWordIDs, dictionaryWordIDs) {
  switch (filterID) {
    case 'new':
      return words.filter((item) => !viewedWordIDs.has(item.id));
    case 'saved':
      return words.filter((item) => dictionaryWordIDs.has(item.id));
    default:
      return words;
  }
}

function emptyFilterText(filterID) {
  switch (filterID) {
    case 'new':
      return 'Новых слов в этой категории уже не осталось. Откройте сохранённые или начните тренировку.';
    case 'saved':
      return 'Пока нет сохранённых слов из этой категории. Добавляйте интересные слова прямо из списка.';
    default:
      return 'В этой категории пока нет слов.';
  }
}

function CategoryAvatar({ category }) {
  if (!category?.image) {
    return <Avatar size={72} gradientColor="blue"><Icon28SchoolOutline /></Avatar>;
  }

  return <Avatar size={72} src={categoryImagePath(category.image)} />;
}

function CategorySkeleton() {
  return (
    <Group>
      <Div className="space-y-3">
        <Skeleton width="45%" height={28} />
        <Skeleton width="80%" height={18} />
        <Skeleton width="65%" height={18} />
        <Skeleton height={44} />
      </Div>
    </Group>
  );
}

export function CategoryPanel({
  nav = CATEGORY_PANEL,
  category,
  categoryActivity,
  categoryWords,
  dictionaryWordIDs,
  loading,
  onBack,
  openWord,
  startTraining,
  purchaseCategory,
  toggleDictionaryWord
}) {
  const [filterID, setFilterID] = useState('all');

  useEffect(() => {
    setFilterID('all');
  }, [category?.id]);

  useEffect(() => {
    if (!category?.id || categoryWords.length === 0) {
      return;
    }

    const top = consumeCategoryScrollPosition(category.id);
    if (top <= 0) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top, behavior: 'auto' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [category?.id, categoryWords.length]);

  const difficultyMeta = getDifficultyMeta(category?.difficulty);
  const progress = getCategoryProgress(categoryActivity, {
    ...category,
    word_count: categoryWords.length || category?.word_count || 0
  });
  const description = getCategoryDescription(category);
  const viewedWordIDs = useMemo(() => new Set(getViewedWordIDs(categoryActivity, category?.id)), [categoryActivity, category?.id]);
  const savedWordIDs = useMemo(() => dictionaryWordIDs || new Set(), [dictionaryWordIDs]);
  const filteredWords = useMemo(
    () => filterWords(categoryWords, filterID, viewedWordIDs, savedWordIDs),
    [categoryWords, filterID, viewedWordIDs, savedWordIDs]
  );
  const trainingCount = getTrainingCount(categoryActivity, category?.id);
  const isLockedPremium = Boolean(category?.is_premium && !category?.is_purchased);
  const trainingAvailable = !isLockedPremium && categoryWords.length >= 3;
  const newWordsCount = isLockedPremium
    ? Math.max((Number(category?.word_count) || categoryWords.length) - viewedWordIDs.size, 0)
    : categoryWords.filter((item) => !viewedWordIDs.has(item.id)).length;
  const savedWordsCount = categoryWords.filter((item) => savedWordIDs.has(item.id)).length;

  return (
    <Panel nav={nav}>
      <ScreenHeader title={category?.name || 'Категория'} onBack={onBack} />
      <AppShell>
        {loading && categoryWords.length === 0 ? <CategorySkeleton /> : null}

        {!loading || categoryWords.length > 0 ? (
          <>
            <Group>
              <SimpleCell
                before={<CategoryAvatar category={category} />}
                subtitle={description}
                multiline
              >
                {category?.name || 'Категория'}
              </SimpleCell>
              <Div>
                <Text className="mb-2 text-[var(--vkui--color_text_secondary)]">
                  {difficultyMeta.label} · {categoryWords.length} {pluralize(categoryWords.length, ['слово', 'слова', 'слов'])}
                  {isLockedPremium && category?.word_count ? ` из ${category.word_count}` : ''}
                  {trainingCount > 0 ? ` · ${trainingCount} ${pluralize(trainingCount, ['тренировка', 'тренировки', 'тренировок'])}` : ''}
                </Text>
                <Progress value={isLockedPremium ? 0 : progress.percent} />
              </Div>
              <CardGrid size="s">
                <Card mode="outline"><Div><Title level="3">{progress.percent}%</Title><Text className="whitespace-nowrap text-[13px]">Прогресс</Text></Div></Card>
                <Card mode="outline"><Div><Title level="3">{newWordsCount}</Title><Text className="whitespace-nowrap text-[13px]">Новые</Text></Div></Card>
                <Card mode="outline"><Div><Title level="3">{savedWordsCount}</Title><Text className="whitespace-nowrap text-[13px]">В словаре</Text></Div></Card>
              </CardGrid>
              <Div>
                {isLockedPremium ? (
                  <>
                    <Button
                      size="l"
                      stretched
                      before={<Icon28LockOutline />}
                      onClick={() => purchaseCategory(category)}
                    >
                      Открыть за {category?.price_votes || 3} голоса
                    </Button>
                    <Text className="mt-2 text-center text-[var(--vkui--color_text_secondary)]">
                      Сейчас доступно превью. После покупки откроются все слова и тренировка.
                    </Text>
                  </>
                ) : (
                  <Button
                    size="l"
                    stretched
                    before={<Icon28BookOutline />}
                    onClick={() => startTraining('category', category.id, CATEGORY_PANEL)}
                    disabled={!trainingAvailable}
                  >
                    {progress.hasStarted ? 'Продолжить тренировку' : 'Начать тренировку'}
                  </Button>
                )}
                {!isLockedPremium && !trainingAvailable ? (
                  <Text className="mt-2 text-center text-[var(--vkui--color_text_secondary)]">Для тренировки нужно минимум 3 слова.</Text>
                ) : null}
              </Div>
            </Group>

            <Group header={<Header subtitle={isLockedPremium ? 'Превью первых слов перед покупкой' : 'Открывайте карточки и сохраняйте важные слова'}>Слова категории</Header>}>
              {!isLockedPremium ? (
                <Tabs>
                  {filters.map((item) => (
                    <TabsItem key={item.id} selected={filterID === item.id} onClick={() => setFilterID(item.id)}>
                      {item.label}
                    </TabsItem>
                  ))}
                </Tabs>
              ) : null}

              {filteredWords.length > 0 ? (
                <CardGrid size="l" className="mt-3">
                  {filteredWords.map((item) => {
                    const isSaved = savedWordIDs.has(item.id);
                    const isViewed = viewedWordIDs.has(item.id);

                    return (
                      <Card key={item.id} mode="outline">
                        <Div>
                          <Text className="mb-3 text-[var(--vkui--color_text_secondary)]">
                            {isViewed ? 'Просмотрено' : 'Новое'}{isSaved ? ' · В словаре' : ''}
                          </Text>
                          <Title level="2" className="break-words" style={{ fontSize: `${fitTextSize(item.word, 10, 24, 17)}px` }}>
                            {item.word}
                          </Title>
                          <Text className="mt-1 text-[var(--vkui--color_text_secondary)]" style={{ fontSize: `${fitTextSize(`[${item.transcription}]`, 23, 14, 11)}px` }}>
                            [{item.transcription}]
                          </Text>
                          <Text className="mt-3 break-words" style={{ fontSize: `${fitTextSize(item.translation, 14, 15, 12)}px` }}>
                            {item.translation}
                          </Text>
                          <ButtonGroup mode="horizontal" stretched className="mt-4">
                            <Button size="m" mode="secondary" stretched onClick={() => openWord(item, CATEGORY_PANEL, { categoryId: category?.id || 0 })}>
                              Открыть
                            </Button>
                            <Button
                              size="m"
                              mode={isSaved ? 'outline' : 'secondary'}
                              stretched
                              before={isSaved ? <Icon28CheckCircleOutline /> : null}
                              onClick={() => toggleDictionaryWord(item, isSaved)}
                              disabled={isLockedPremium}
                            >
                              {isLockedPremium ? 'После покупки' : isSaved ? 'Сохранено' : 'В словарь'}
                            </Button>
                          </ButtonGroup>
                        </Div>
                      </Card>
                    );
                  })}
                </CardGrid>
              ) : !loading ? <EmptyState text={emptyFilterText(filterID)} /> : null}
            </Group>
          </>
        ) : null}
      </AppShell>
    </Panel>
  );
}
